import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/profile-components/Navbar";
import Topbar from "../../components/profile-components/Topbar";
import EmailEditor from "react-email-editor";
import "../../styles/EditorWorkspace.css";

import MERGE_TAGS from "../../constants/mergeTags";
import { flattenMergeTagsForUnlayer } from "../../utils/flattenMergeTags";
import MergeTagPicker from "../../components/merge-tags/MergeTagPicker";
import { authFetch } from "../../utils/authFetch";

const UNLAYER_OPTIONS = {
  projectId: 277125,
  appearance: { theme: "modern" },
  features: {
    stockImages: true,
    undoRedo: true,
    preview: true,
  },
};

export default function SystemEmailEditor() {
  const navigate = useNavigate();
  const { id } = useParams();

  const isNew = !id || id === "new";

  const editorRef = useRef(null);
  const subjectRef = useRef(null);

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [showSubjectTags, setShowSubjectTags] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editorReady, setEditorReady] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(!isNew);
  const [designApplied, setDesignApplied] = useState(false);
  const [loadedTemplate, setLoadedTemplate] = useState(null);

  const FLAT_MERGE_TAGS = flattenMergeTagsForUnlayer(MERGE_TAGS);

  const insertAtCursor = (ref, value) => {
    const el = ref.current;
    if (!el) return;

    const start = el.selectionStart;
    const end = el.selectionEnd;

    setSubject(el.value.slice(0, start) + value + el.value.slice(end));

    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + value.length, start + value.length);
    });

    setShowSubjectTags(false);
  };

  const handleForbidden = (status) => {
    if (status === 403) {
      alert("You do not have permission to access System Emails.");
      navigate("/dashboard");
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (isNew) {
      setTemplateLoading(false);
      return;
    }

    let isMounted = true;

    async function loadSystemEmail() {
      try {
        setTemplateLoading(true);

        const res = await authFetch(`/api/system-emails/${id}`);

        if (handleForbidden(res.status)) return;

        if (!res.ok) {
          throw new Error("Failed to load system email");
        }

        const tpl = await res.json();

        if (!isMounted) return;

        setLoadedTemplate(tpl);
        setName(tpl.name || "");
        setSubject(tpl.subject || "");
      } catch (err) {
        console.error("Load system email failed:", err);
      } finally {
        if (isMounted) {
          setTemplateLoading(false);
        }
      }
    }

    loadSystemEmail();

    return () => {
      isMounted = false;
    };
  }, [id, isNew, navigate]);

  useEffect(() => {
    if (!editorReady) return;
    if (!editorRef.current?.editor) return;
    if (isNew) return;
    if (!loadedTemplate) return;
    if (designApplied) return;

    try {
      const rawDesign =
        loadedTemplate.design_json ??
        loadedTemplate.designJson ??
        (loadedTemplate.body && loadedTemplate.body.includes('"body":')
          ? loadedTemplate.body
          : null);

      if (rawDesign) {
        const parsed =
          typeof rawDesign === "string" ? JSON.parse(rawDesign) : rawDesign;

        editorRef.current.editor.loadDesign(parsed);
      }

      setDesignApplied(true);
    } catch (err) {
      console.error("Design JSON parse failed:", err);
    }
  }, [editorReady, isNew, loadedTemplate, designApplied]);

  const onReady = (editor) => {
    editor.setMergeTags(FLAT_MERGE_TAGS);
    setEditorReady(true);
  };

  const handleSave = () => {
    if (!editorRef.current?.editor) {
      alert("Editor not ready yet.");
      return;
    }

    if (!name.trim()) {
      alert("Name is required.");
      return;
    }

    setSaving(true);

    editorRef.current.editor.exportHtml(async ({ design, html }) => {
      try {
        const payload = {
          name: name.trim(),
          subject,
          body: html,
          design_json: JSON.stringify(design),
        };

        const url = isNew
          ? "/api/system-emails"
          : `/api/system-emails/${id}`;

        const method = isNew ? "POST" : "PUT";

        const res = await authFetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (handleForbidden(res.status)) return;

        if (!res.ok) {
          const errText = await res.text();
          console.error("System email save failed:", errText);
          throw new Error("Save failed");
        }

        navigate("/settings/system-emails");
      } catch (err) {
        alert(err.message);
      } finally {
        setSaving(false);
      }
    });
  };

  const showLoadingOverlay = !editorReady || templateLoading;

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page template-editor-page">
        <div className="sales-hero template-editor-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">
              {isNew ? "Add System Email" : "Edit System Email"}
            </h1>
            <p className="sales-subtitle">
              Manage platform-level system emails, branding, and merge-tag-driven messaging.
            </p>
          </div>

          <div className="sales-header-actions">
            <button
              className="sales-secondary-btn"
              onClick={() => navigate("/settings/system-emails")}
            >
              Back to System Emails
            </button>

            <button
              className="sales-primary-btn"
              onClick={handleSave}
              disabled={saving || showLoadingOverlay}
            >
              {saving
                ? isNew
                  ? "Creating..."
                  : "Saving..."
                : isNew
                  ? "Create System Email"
                  : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="template-editor-grid">
          <div className="sales-section-card template-editor-sidebar">
            <div className="sales-section-head">
              <h2>System Email Details</h2>
              <p>Internal name, subject line, and platform merge tags.</p>
            </div>

            <div className="template-editor-fields">
              <div className="template-field">
                <label>Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter internal system email name"
                />
              </div>

              <div className="template-field template-subject-field">
                <label>Subject Line</label>

                <input
                  ref={subjectRef}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter subject line"
                />

                <button
                  type="button"
                  className="template-inline-link"
                  onClick={() => setShowSubjectTags((prev) => !prev)}
                >
                  Insert merge tag
                </button>

                {showSubjectTags && (
                  <div className="template-merge-picker-wrap">
                    <MergeTagPicker
                      onInsert={(tag) => insertAtCursor(subjectRef, tag)}
                      onClose={() => setShowSubjectTags(false)}
                    />
                  </div>
                )}
              </div>

              <div className="template-helper-card">
                <h3>Quick Tips</h3>
                <ul>
                  <li>Use clear internal names so admins can identify each system email quickly.</li>
                  <li>Keep system email subjects direct and recognizable.</li>
                  <li>Use merge tags to personalize onboarding, reminders, and account notices.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="sales-section-card template-editor-main">
            <div className="sales-section-head">
              <h2>Email Designer</h2>
              <p>Design the system email layout and content.</p>
            </div>

            <div
              className="template-editor-canvas"
              style={{ position: "relative" }}
            >
              {showLoadingOverlay && (
                <div className="template-editor-loading">
                  {templateLoading ? "Loading system email..." : "Loading editor..."}
                </div>
              )}

              <EmailEditor
                ref={editorRef}
                onReady={onReady}
                options={UNLAYER_OPTIONS}
                style={{ minHeight: 640 }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}